import { Component, OnInit } from '@angular/core';
import { AnnoncenewService } from '../service/annoncenew.service';
import { Annonce } from '../models/annonce.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

@Component({
  selector: 'app-listnewannonce',
  templateUrl: './listnewannonce.component.html',
  styleUrls: ['./listnewannonce.component.scss']
})
export class ListnewannonceComponent implements OnInit {

  allAnnonces: Annonce[] = [];
  annonces: Annonce[] = [];

  totalItems = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 20, 50];
  isLoading = false;

  filterForm: FormGroup;
  searchTerm = '';

  cities: string[] = [
    'Tunis', 'Ariana', 'Ben Arous', 'Manouba', 'Nabeul', 'Zaghouan',
    'Bizerte', 'Béja', 'Jendouba', 'Le Kef', 'Siliana', 'Kairouan',
    'Kasserine', 'Sidi Bouzid', 'Sousse', 'Monastir', 'Mahdia',
    'Sfax', 'Gabès', 'Médenine', 'Tataouine', 'Gafsa', 'Tozeur', 'Kebili'
  ];

  currentPage = 0;

  // Clé pour stocker l'état dans sessionStorage
  private readonly STATE_KEY = 'annonceListState';

  constructor(
    private annonceService: AnnoncenewService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    this.filterForm = this.fb.group({
      ville: ['']
    });
  }

  ngOnInit(): void {
    // Restaurer l'état depuis sessionStorage
    this.restoreState();
    this.loadAllAnnonces();
  }

  // 📦 Chargement de TOUTES les annonces (sans pagination backend)
  loadAllAnnonces(): void {
    this.isLoading = true;
    this.annonceService.getAllAnnonces().subscribe({
      next: (data: Annonce[]) => {
        // ✅ Tri par ID décroissant (les plus récentes en premier)
        this.allAnnonces = data.sort((a, b) => (b.id || 0) - (a.id || 0));
        this.applyFiltersAndPagination();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erreur de chargement :', error);
        this.snackBar.open('Erreur lors du chargement des annonces', 'Fermer', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isLoading = false;
      }
    });
  }

  // 🔍 Applique les filtres puis met à jour la pagination
  applyFiltersAndPagination(): void {
    const villeValue = this.filterForm.get('ville')?.value?.trim() || '';
    const searchValue = this.searchTerm?.trim() || '';

    // Filtrer
    let filtered = this.allAnnonces;
    if (searchValue) {
      filtered = filtered.filter(a =>
        a.annonceName?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    if (villeValue) {
      filtered = filtered.filter(a => a.ville === villeValue);
    }

    this.totalItems = filtered.length;

    // Paginer
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    this.annonces = filtered.slice(start, end);

    // ✅ Sauvegarder l'état courant
    this.saveState();
  }

  // 📄 Pagination
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyFiltersAndPagination();
  }

  // 🔍 Application des filtres (repart à la page 0)
  applyFilters(): void {
    this.currentPage = 0;
    this.applyFiltersAndPagination();
  }

  // 🧹 Efface tous les filtres
  clearFilters(): void {
    this.searchTerm = '';
    this.filterForm.reset({ ville: '' });
    this.currentPage = 0;
    // On supprime aussi l'état stocké
    sessionStorage.removeItem(this.STATE_KEY);
    this.applyFiltersAndPagination();
  }

  // 🔎 Recherche par nom
  searchAnnonces(): void {
    this.currentPage = 0;
    this.applyFiltersAndPagination();
  }

  // ➕ Navigation vers la page d'ajout
  goToAddAnnonce(): void {
    // L'état est déjà sauvegardé à chaque changement, donc pas besoin de le faire ici
    this.router.navigate(['/addNewAnnonce']);
  }

  // ✏️ Navigation vers la page d'édition
  goToEditAnnonce(id: number): void {
    this.router.navigate(['/editNewAnnonce', id]);
  }

  // 🗑️ Suppression avec confirmation
  deleteAnnonce(id: number, name: string): void {
    const confirmed = confirm(`Supprimer définitivement "${name}" ?`);
    if (!confirmed) return;

    this.isLoading = true;
    this.annonceService.deleteAnnonce(id).subscribe({
      next: () => {
        this.snackBar.open(`"${name}" supprimée avec succès`, 'Fermer', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.loadAllAnnonces(); // Recharge la liste (l'état sera restauré)
      },
      error: (error) => {
        console.error('Erreur de suppression :', error);
        this.snackBar.open('Erreur lors de la suppression', 'Fermer', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isLoading = false;
      }
    });
  }

  // 🖼️ Récupère l'URL de l'image (avec fallback)
  getImageUrl(imagePath: string | undefined): string {
    if (!imagePath) return 'assets/images/default-annonce.jpg';
    return imagePath.startsWith('http') ? imagePath : `https://app.titv-store-api.com${imagePath}`;
  }

  // 🎨 Couleur du badge en fonction du type
  getTypeColor(type: string): string {
    const colors: { [key: string]: string } = {
      'Vente': '#2196f3',
      'Location': '#4caf50',
      'Echange': '#ff9800',
      'Autre': '#9c27b0'
    };
    return colors[type] || '#757575';
  }

  // ========== PERSISTANCE DE L'ÉTAT ==========

  private saveState(): void {
    const state = {
      searchTerm: this.searchTerm,
      ville: this.filterForm.get('ville')?.value || '',
      currentPage: this.currentPage,
      pageSize: this.pageSize
    };
    sessionStorage.setItem(this.STATE_KEY, JSON.stringify(state));
  }

  private restoreState(): void {
    const stored = sessionStorage.getItem(this.STATE_KEY);
    if (stored) {
      try {
        const state = JSON.parse(stored);
        this.searchTerm = state.searchTerm || '';
        this.currentPage = state.currentPage || 0;
        this.pageSize = state.pageSize || 10;
        if (state.ville) {
          this.filterForm.patchValue({ ville: state.ville });
        }
      } catch (e) {
        console.warn('Erreur lors de la restauration de l\'état', e);
        sessionStorage.removeItem(this.STATE_KEY);
      }
    }
  }
}